import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { ArrowLeft, Sparkle, CircleNotch, TrendUp, ShieldCheck, Clock, Warning, Robot, Info, ArrowRight } from "@phosphor-icons/react";

const WINDOWS = [
  { key: 7, label: "7 days" },
  { key: 30, label: "30 days" },
  { key: 90, label: "90 days" },
  { key: 365, label: "365 days" },
];

function KpiCard({ label, value, unit, sub, testId, color = "emerald", icon: Icon }) {
  const rings = {
    emerald: "border-emerald-200 bg-emerald-50/40 text-emerald-900",
    slate: "border-zinc-200 bg-white text-slate-900",
    amber: "border-amber-200 bg-amber-50/40 text-amber-900",
    rose: "border-rose-200 bg-rose-50/40 text-rose-900",
  };
  return (
    <div className={`rounded-lg border p-4 hover:shadow-md transition-shadow duration-200 ${rings[color]}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && <Icon size={14} className="text-slate-400" />}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-heading text-3xl font-semibold" style={{ fontFamily: "'Cardo', Georgia, serif" }}>{value}</span>
        {unit && <span className="text-sm text-slate-500 font-medium">{unit}</span>}
      </div>
      {sub && <p className="text-[10px] text-slate-500 mt-1.5">{sub}</p>}
    </div>
  );
}

function BarTrend({ points, testId }) {
  const max = Math.max(1, ...points.map((p) => p.fields));
  return (
    <div className="flex items-end gap-1 h-16" data-testid={testId}>
      {points.length === 0 && <p className="text-[10px] text-zinc-400 italic self-center">No packet activity in this window yet.</p>}
      {points.map((p) => (
        <div key={p.date} className="flex-1 flex flex-col items-center group relative">
          <div className="w-full bg-slate-900 rounded-t-sm hover:bg-emerald-600 transition-colors duration-200" style={{ height: `${(p.fields / max) * 100}%`, minHeight: p.fields ? 4 : 1 }} />
          <span className="text-[8px] text-zinc-400 mt-0.5">{p.date.slice(5)}</span>
          <div className="absolute bottom-full mb-1 px-1.5 py-0.5 bg-slate-900 text-white text-[9px] rounded opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap font-mono">{p.fields} fields · {p.packets} packet{p.packets === 1 ? "" : "s"}</div>
        </div>
      ))}
    </div>
  );
}

export default function DrakeMetricsDashboard() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({
    queryKey: ["drake-metrics", days],
    queryFn: () => api.get(`/drake/metrics?days=${days}`).then((r) => r.data),
    refetchInterval: 30000,
  });
  const kpi = data?.kpis || {};
  const noData = !isLoading && (kpi.total_fields || 0) === 0;

  const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);
  const fmtSec = (v) => {
    if (!v && v !== 0) return "—";
    if (v < 60) return `${Math.round(v)}s`;
    if (v < 3600) return `${(v / 60).toFixed(1)}m`;
    return `${(v / 3600).toFixed(1)}h`;
  };

  return (
    <div className="min-h-screen bg-zinc-50 text-slate-900" data-testid="drake-metrics-dashboard">
      <header className="bg-white border-b border-zinc-200 px-6 py-3 flex items-center justify-between sticky top-0 z-30 shadow-sm">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" data-testid="metrics-back-btn" className="text-zinc-500 hover:text-slate-900 transition-colors duration-150"><ArrowLeft size={16} /></Link>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold flex items-center gap-1"><Sparkle size={11} weight="fill" /> Drake Pipeline · Success Metrics</p>
            <h1 className="font-heading text-lg text-slate-900" style={{ fontFamily: "'Cardo', Georgia, serif" }}>Field Success Dashboard</h1>
            <p className="text-[10px] text-zinc-500">Live autofill, override &amp; touch-time KPIs across every extraction.</p>
          </div>
        </div>
        <div className="flex items-center gap-1 border border-zinc-200 rounded-md bg-white p-0.5" data-testid="window-picker">
          {WINDOWS.map((w) => (
            <button key={w.key} data-testid={`window-${w.key}`} onClick={() => setDays(w.key)}
              className={`text-[11px] px-2.5 py-1 rounded transition-colors duration-150 ${days === w.key ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-zinc-50"}`}>
              {w.label}
            </button>
          ))}
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto p-6 space-y-6">
        {isLoading && (
          <div className="rounded-lg border border-zinc-200 bg-white p-10 text-center"><CircleNotch size={22} className="animate-spin text-zinc-400 mx-auto" /></div>
        )}

        {noData && (
          <div className="rounded-lg border border-dashed border-zinc-300 bg-white px-4 py-10 text-center" data-testid="metrics-empty">
            <Robot size={28} className="text-zinc-400 mx-auto mb-2" />
            <p className="text-sm text-slate-800 font-medium">No pipeline activity in the last {days} days</p>
            <p className="text-[11px] text-zinc-500 mt-1">Extract a document in any client's Tax Data Room to start populating this dashboard.</p>
          </div>
        )}

        {!isLoading && !noData && (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard testId="kpi-autofill" label="Autofill rate" value={fmtPct(kpi.autofill_rate_pct)} sub={`${kpi.fields_written_to_drake || 0} fields written to Drake`} icon={ShieldCheck} color="emerald" />
              <KpiCard testId="kpi-override" label="Override rate" value={fmtPct(kpi.override_rate_pct)} sub="% of auto-accepted fields a preparer changed" icon={Warning} color={kpi.override_rate_pct > 5 ? "amber" : "slate"} />
              <KpiCard testId="kpi-touch" label="Avg review touch time" value={fmtSec(kpi.avg_review_touch_sec)} sub={`Across ${kpi.total_fields || 0} fields extracted`} icon={Clock} color="slate" />
              <KpiCard testId="kpi-backlog" label="Needs-review backlog" value={String(kpi.needs_review_backlog || 0)} sub={`${kpi.total_packets || 0} packets exported`} icon={TrendUp} color={kpi.needs_review_backlog > 10 ? "amber" : "slate"} />
            </div>

            {/* Trend */}
            <div className="rounded-lg border border-zinc-200 bg-white p-5 hover:shadow-md transition-shadow duration-200" data-testid="metrics-trend">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">Daily field extraction volume</p>
                  <p className="text-sm font-medium text-slate-900">Pipeline activity trend</p>
                </div>
                <span className="text-[10px] text-zinc-500">Hover a bar for details</span>
              </div>
              <BarTrend points={data.trend} testId="metrics-trend-chart" />
            </div>

            {/* Per-form breakdown */}
            <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden hover:shadow-md transition-shadow duration-200" data-testid="metrics-by-form">
              <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                <p className="text-sm font-medium text-slate-900">Per-form performance</p>
                <p className="text-[10px] text-zinc-500 font-mono">{data.by_form.length} form types</p>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-100 bg-zinc-50">
                    <th className="text-left px-4 py-2 font-medium text-slate-600 text-[10px] uppercase tracking-wider">Form</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-600 text-[10px] uppercase tracking-wider">Total fields</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-600 text-[10px] uppercase tracking-wider">Auto-accepted</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-600 text-[10px] uppercase tracking-wider">Confirmed</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-600 text-[10px] uppercase tracking-wider">Overridden</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-600 text-[10px] uppercase tracking-wider">Needs review</th>
                    <th className="text-right px-4 py-2 font-medium text-slate-600 text-[10px] uppercase tracking-wider">Autofill %</th>
                    <th className="text-right px-4 py-2 font-medium text-slate-600 text-[10px] uppercase tracking-wider">Override %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_form.map((f) => (
                    <tr key={f.form_type} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50 transition-colors duration-100" data-testid={`metric-row-${f.form_type}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-900">{f.form_type}</td>
                      <td className="text-right px-3 py-2.5 font-mono tabular-nums">{f.total}</td>
                      <td className="text-right px-3 py-2.5 font-mono tabular-nums text-emerald-700">{f.auto_accepted}</td>
                      <td className="text-right px-3 py-2.5 font-mono tabular-nums text-emerald-700">{f.preparer_confirmed}</td>
                      <td className="text-right px-3 py-2.5 font-mono tabular-nums text-indigo-700">{f.preparer_overridden}</td>
                      <td className="text-right px-3 py-2.5 font-mono tabular-nums text-amber-700">{f.needs_review}</td>
                      <td className="text-right px-4 py-2.5">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-14 h-1 bg-zinc-100 rounded-full overflow-hidden">
                            <div className={`h-full ${f.autofill_rate >= 85 ? "bg-emerald-500" : f.autofill_rate >= 60 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${f.autofill_rate}%` }} />
                          </div>
                          <span className="font-mono tabular-nums text-slate-800 w-10 text-right">{f.autofill_rate}%</span>
                        </div>
                      </td>
                      <td className="text-right px-4 py-2.5 font-mono tabular-nums text-slate-700">{f.override_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Write-discrepancy note per design doc §8 */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/50 px-4 py-3 flex items-start gap-2" data-testid="write-discrepancy-note">
              <Info size={14} className="text-amber-700 shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-amber-900">Write-discrepancy rate · Not yet measured</p>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  Requires the Drake export/read-back loop from §L5 of the design doc — currently blocked on a Drake / GruntWorx partner agreement for programmatic return export.
                  Once that access is in place, we'll auto-diff the exported return against our canonical model and land the fifth KPI here.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
